import { Typography } from '@material-ui/core';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import React, { useEffect } from 'react';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import {
  clearUnregistrationSuccessMessage,
  fetchActiveNotifications,
  fetchNotificationsRuleData,
  sendUnregisterNotification,
  updateNotificationActive,
} from '../../store/Notifications/NotificationsActions';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import StringResourceManager, { GetString } from '../StringResourceManager';
import { Button, Checkbox, ListItemIcon } from '@mui/material';
import { createStyles, makeStyles } from '@material-ui/core';
import {
  Notification,
  NotificationNameActiveMap,
} from '../../Interfaces/Notifications/Notification';
import { Fade } from '@progress/kendo-react-animation';
import {
  Notification as KendoNotification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import { LoadingPanel } from '../LoadingPanel';

const useStyles = makeStyles(() =>
  createStyles({
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    send: {
      fontSize: '1.5rem',
    },
  })
);

interface Props {
  notificationType: string;
  handleEditNotification: (notificationName: string) => void;
}

const RegisteredNotificationsListBox: React.FC<Props> = ({
  notificationType,
  handleEditNotification,
}): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [successMessage, displaySuccessMessage] = React.useState(false);

  const UNREGISTER_USER_TITLE = GetString('UNREGISTER_NOTIFICATION_TITLE');
  const UNREGISTER_CONFIRMATION_MESSAGE = GetString(
    'NOTIFICATION_UNREGISTER_CONFIRMATION_MESSAGE'
  );
  const YES = GetString('YES');
  const NO = GetString('NO');

  const unregistrationStatus: string = useSelector(
    (state: ApplicationState) =>
      state.notificationsReducer.unregistrationSuccessMessage
  );
  const notificationRuleNames: string[] = useSelector(
    (state: ApplicationState) =>
      state.notificationsReducer.notificationRuleNames
  );
  const notificationData: Notification = useSelector(
    (state: ApplicationState) => state.notificationsReducer.notificationRuleData
  );
  const loading: boolean = useSelector(
    (state: ApplicationState) =>
      state.notificationsReducer.isFetchingNotificationNames
  );
  const notificationNameActiveMap: NotificationNameActiveMap[] = useSelector(
    (state: ApplicationState) => state.notificationsReducer.activeNotifications
  );

  useEffect((): ReturnType<React.EffectCallback> => {
    fetchActiveNotifications(dispatch, notificationType);
    return (): void => {};
  }, [notificationType]);

  const handleEdit = (name: string) => () => {
    handleEditNotification(name);
  };
  const handleDelete = (name: string) => () => {
    fetchNotificationsRuleData(dispatch, notificationType, name);
    setShowDeleteDialog(true);
  };
  const unregisterNotification = () => {
    let payload: Notification = notificationData;
    sendUnregisterNotification(dispatch, payload).then(() =>
      fetchActiveNotifications(dispatch, notificationType)
    );
    displaySuccessMessage(true);
    handleClose();
  };
  const handleClose = () => {
    setShowDeleteDialog(false);
  };
  const resetSuccessMessage = () => {
    clearUnregistrationSuccessMessage(dispatch);
  };

  const handleCheckboxChange = (index: number) => {
    updateNotificationActive(dispatch, notificationType, index);
  };

  useEffect(() => {
    let timeoutId: any;
    if (successMessage) {
      timeoutId = setTimeout(() => {
        displaySuccessMessage(false);
        resetSuccessMessage();
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [successMessage]);

  return (
    <React.Fragment>
      <List
        sx={{
          width: '100%',
          height: 380,
          maxWidth: 360,
          bgcolor: 'background.paper',
          overflow: 'auto',
        }}
      >
        {loading && LoadingPanel}
        {notificationNameActiveMap.map((notification, index) => (
          <ListItem
            key={index}
            sx={{
              backgroundColor: notification.NotificationActive
                ? 'inherit'
                : 'lightgrey',
            }}
          >
            <ListItemIcon>
              <Checkbox
                edge="start"
                disableRipple
                checked={notification.NotificationActive}
                onChange={() => handleCheckboxChange(index)}
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography
                  variant="body1"
                  style={{
                    whiteSpace: 'normal',
                    overflow: 'hidden',
                    marginRight: '10px',
                    maxWidth: '230px',
                    color: notification.NotificationActive ? 'inherit' : 'grey',
                  }}
                >
                  {notification.NotificationName}
                </Typography>
              }
            ></ListItemText>
            <IconButton onClick={handleEdit(notification.NotificationName)}>
              <EditIcon />
            </IconButton>
            <IconButton onClick={handleDelete(notification.NotificationName)}>
              <DeleteIcon />
            </IconButton>
          </ListItem>
        ))}
      </List>
      {showDeleteDialog && (
        <Dialog onClose={handleClose} title={UNREGISTER_USER_TITLE}>
          <Typography variant="body1">
            {UNREGISTER_CONFIRMATION_MESSAGE}
          </Typography>
          <DialogActionsBar layout="center">
            <Button
              color="primary"
              variant="contained"
              onClick={unregisterNotification}
            >
              {YES}
            </Button>
            <Button color="primary" variant="contained" onClick={handleClose}>
              {NO}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
      <Fade>
        <NotificationGroup className={classes.bottomRight}>
          {successMessage && unregistrationStatus && (
            <KendoNotification
              className={classes.send}
              type={{
                style: unregistrationStatus === 'success' ? 'success' : 'error',
                icon: true,
              }}
              closable={true}
            >
              {
                <StringResourceManager
                  stringKey={
                    unregistrationStatus === 'success'
                      ? 'NOTIFICATION_UNREGISTRATION_SUCCESS_MESSAGE'
                      : 'NOTIFICATION_UNREGISTRATION_FAILED_MESSAGE'
                  }
                />
              }
            </KendoNotification>
          )}
        </NotificationGroup>
      </Fade>
    </React.Fragment>
  );
};

export default RegisteredNotificationsListBox;
